import { useState, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Upload, Sparkles, Plus, X, FileText, ArrowLeft, ArrowRight,
  Check, AlertTriangle, Loader2,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getSecondaryDefaultModifiers } from '@/lib/modifiers/xepsu';

interface TemplateWizardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

interface CptEntry {
  code: string;
  description: string;
  section: 'primary' | 'secondary';
  default_modifiers: string[];
}

interface DiagnosisEntry {
  code: string;
  description: string;
}

const SPECIALTIES = [
  'orthopedics', 'general_surgery', 'neurosurgery', 'cardiothoracic',
  'vascular', 'urology', 'gynecology', 'ent', 'plastics', 'other',
];

const BODY_REGIONS = [
  'knee', 'shoulder', 'hip', 'wrist', 'ankle', 'spine',
  'elbow', 'hand', 'foot', 'pelvis', 'chest', 'abdomen', 'other',
];

const WIZARD_STEPS = [
  { label: 'Upload Note', icon: Upload },
  { label: 'Template Info', icon: FileText },
  { label: 'Review & Edit', icon: Check },
];

export function TemplateWizardDialog({ open, onOpenChange, onCreated }: TemplateWizardDialogProps) {
  const [step, setStep] = useState(1);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step 1 — Upload
  const [uploadMethod, setUploadMethod] = useState<'paste' | 'file'>('paste');
  const [pastedText, setPastedText] = useState('');
  const [fileName, setFileName] = useState('');
  const [isProcessingFile, setIsProcessingFile] = useState(false);

  // Step 2 — Template Info
  const [name, setName] = useState('');
  const [specialty, setSpecialty] = useState('orthopedics');
  const [bodyRegion, setBodyRegion] = useState('');
  const [lateralityRequired, setLateralityRequired] = useState(false);

  // Step 3 — Review
  const [cptEntries, setCptEntries] = useState<CptEntry[]>([]);
  const [diagnosisCodes, setDiagnosisCodes] = useState<DiagnosisEntry[]>([]);
  const [narrativeTemplate, setNarrativeTemplate] = useState('');
  const [commonAddons, setCommonAddons] = useState<CptEntry[]>([]);
  const [newCode, setNewCode] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newSection, setNewSection] = useState<'primary' | 'secondary'>('primary');
  const [isExtracting, setIsExtracting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [extractionDone, setExtractionDone] = useState(false);

  const resetWizard = () => {
    setStep(1);
    setUploadMethod('paste');
    setPastedText('');
    setFileName('');
    setName('');
    setSpecialty('orthopedics');
    setBodyRegion('');
    setLateralityRequired(false);
    setCptEntries([]);
    setDiagnosisCodes([]);
    setNarrativeTemplate('');
    setCommonAddons([]);
    setExtractionDone(false);
  };

  // File upload handler
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File too large. Max 10MB.');
      return;
    }

    setIsProcessingFile(true);
    setFileName(file.name);

    try {
      if (file.type === 'text/plain') {
        const text = await file.text();
        setPastedText(text);
      } else {
        // For PDF/DOCX, read as text (basic extraction)
        // In production this would use a document parsing service
        const text = await file.text();
        if (text && text.length > 50) {
          setPastedText(text);
        } else {
          toast.info('Could not extract text from file. Please paste the note text manually.');
          setUploadMethod('paste');
        }
      }
    } catch {
      toast.error('Failed to read file. Please paste the text instead.');
      setUploadMethod('paste');
    }

    setIsProcessingFile(false);
  };

  // AI extraction (deprecated — template extraction agent removed)
  const handleExtract = async () => {
    if (!pastedText.trim()) {
      toast.error('Please provide operative note text first.');
      return;
    }
    toast.error('AI template extraction is being upgraded. Please add CPTs manually for now.');
  };

  // Manual CPT add
  const addManualCpt = () => {
    if (!newCode.trim()) return;
    setCptEntries(prev => [
      ...prev,
      {
        code: newCode.trim(),
        description: newDesc.trim(),
        section: newSection,
        default_modifiers: newSection === 'secondary' ? getSecondaryDefaultModifiers() : [],
      },
    ]);
    setNewCode('');
    setNewDesc('');
  };

  const removeCpt = (index: number) => {
    setCptEntries(prev => prev.filter((_, i) => i !== index));
  };

  const toggleCptSection = (index: number) => {
    setCptEntries(prev =>
      prev.map((c, i) => {
        if (i !== index) return c;
        const newSec = c.section === 'primary' ? 'secondary' : 'primary';
        return {
          ...c,
          section: newSec,
          default_modifiers: newSec === 'secondary' ? getSecondaryDefaultModifiers(c.default_modifiers) : [],
        };
      })
    );
  };

  // Save template
  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Template name is required.');
      return;
    }
    if (cptEntries.length === 0) {
      toast.error('At least one CPT code is required.');
      return;
    }

    setIsSaving(true);

    const primaryCpts = cptEntries
      .filter(c => c.section === 'primary')
      .map(c => ({ code: c.code, description: c.description }));

    const secondaryCpts = cptEntries
      .filter(c => c.section === 'secondary')
      .map(c => ({
        code: c.code,
        description: c.description,
        default_modifiers: getSecondaryDefaultModifiers(c.default_modifiers),
      }));

    const finalNarrative = narrativeTemplate || pastedText || null;

    const { data: inserted, error } = await supabase
      .from('surgeon_templates')
      .insert({
        surgeon_id: 'demo-surgeon',
        template_name: name,
        specialty,
        body_region: bodyRegion || null,
        laterality_required: lateralityRequired,
        primary_cpts: primaryCpts as any,
        secondary_cpts: secondaryCpts as any,
        narrative_template: finalNarrative,
        common_addons: commonAddons.map(c => ({ code: c.code, description: c.description })) as any,
        diagnosis_codes: diagnosisCodes as any,
        is_demo: false,
        version: 1,
      } as any)
      .select('id')
      .maybeSingle();

    if (error) {
      toast.error('Failed to save template: ' + error.message);
      setIsSaving(false);
      return;
    }

    // Create initial version record for audit
    if (inserted?.id) {
      await supabase.from('surgeon_template_versions').insert({
        template_id: inserted.id,
        version: 1,
        primary_cpts: primaryCpts,
        secondary_cpts: secondaryCpts,
        narrative_template: finalNarrative,
        common_addons: commonAddons.map(c => ({ code: c.code, description: c.description })),
        change_description: 'Initial template creation via Template Wizard',
        changed_by: 'surgeon',
      });
    }

    setIsSaving(false);
    toast.success(`Template "${name}" created (v1)`);
    onCreated();
    onOpenChange(false);
    resetWizard();
  };

  const canProceedStep1 = pastedText.trim().length > 0;
  const canProceedStep2 = name.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) resetWizard(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg">Template Wizard</DialogTitle>
          {/* Step indicators */}
          <div className="flex items-center gap-2 pt-2">
            {WIZARD_STEPS.map((s, i) => {
              const StepIcon = s.icon;
              const stepNum = i + 1;
              const isActive = step === stepNum;
              const isDone = step > stepNum;
              return (
                <div key={i} className="flex items-center gap-1.5">
                  {i > 0 && <div className={`w-8 h-px ${isDone ? 'bg-primary' : 'bg-border'}`} />}
                  <div
                    className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : isDone
                        ? 'bg-primary/10 text-primary'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {isDone ? <Check className="w-3 h-3" /> : <StepIcon className="w-3 h-3" />}
                    <span className="hidden sm:inline">{s.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogHeader>

        {/* Step 1: Upload Note */}
        {step === 1 && (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Upload or paste a prior operative note. LINK will extract CPT codes, modifiers, and narrative structure.
            </p>

            <Tabs value={uploadMethod} onValueChange={v => setUploadMethod(v as 'paste' | 'file')}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="paste">
                  <FileText className="w-3.5 h-3.5 mr-1.5" />
                  Paste Text
                </TabsTrigger>
                <TabsTrigger value="file">
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  Upload File
                </TabsTrigger>
              </TabsList>

              <TabsContent value="paste" className="mt-3">
                <Textarea
                  value={pastedText}
                  onChange={e => setPastedText(e.target.value)}
                  placeholder="Paste operative note text here…"
                  className="min-h-[220px] font-mono text-xs"
                />
              </TabsContent>

              <TabsContent value="file" className="mt-3">
                <div
                  className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {isProcessingFile ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                      <p className="text-sm text-muted-foreground">Processing file…</p>
                    </div>
                  ) : fileName ? (
                    <div className="flex flex-col items-center gap-2">
                      <FileText className="w-8 h-8 text-primary" />
                      <p className="text-sm font-medium">{fileName}</p>
                      <p className="text-xs text-muted-foreground">
                        {pastedText.length} characters extracted
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-8 h-8 text-muted-foreground" />
                      <p className="text-sm font-medium">Drop file or click to upload</p>
                      <p className="text-xs text-muted-foreground">PDF, DOCX, or TXT (max 10MB)</p>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.txt"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </TabsContent>
            </Tabs>

            {pastedText.trim() && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Check className="w-3.5 h-3.5 text-primary" />
                {pastedText.length.toLocaleString()} characters ready for extraction
              </div>
            )}
          </div>
        )}

        {/* Step 2: Template Info & Metadata */}
        {step === 2 && (
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>
                Template Name <span className="text-destructive">*</span>
              </Label>
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Total Knee Arthroplasty"
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Specialty</Label>
                <Select value={specialty} onValueChange={setSpecialty}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SPECIALTIES.map(s => (
                      <SelectItem key={s} value={s}>
                        {s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Body Region</Label>
                <Select value={bodyRegion} onValueChange={setBodyRegion}>
                  <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {BODY_REGIONS.map(r => (
                      <SelectItem key={r} value={r}>
                        {r.charAt(0).toUpperCase() + r.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div>
                <Label>Laterality Required?</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Enables Left / Right / Bilateral selection and applies LT/RT/50 modifiers
                </p>
              </div>
              <Switch checked={lateralityRequired} onCheckedChange={setLateralityRequired} />
            </div>
          </div>
        )}

        {/* Step 3: Review & Edit */}
        {step === 3 && (
          <div className="space-y-4 py-2">
            {/* Extraction action */}
            {!extractionDone && (
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">AI Extraction</p>
                    <p className="text-xs text-muted-foreground">
                      Extract CPTs, modifiers, and narrative from your note
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setExtractionDone(true)}>
                      Skip — Add Manually
                    </Button>
                    <Button size="sm" onClick={handleExtract} disabled={isExtracting}>
                      {isExtracting ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          Extracting…
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                          Extract with AI
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* CPT List */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">CPT Codes</Label>
              {cptEntries.length === 0 && extractionDone && (
                <p className="text-xs text-muted-foreground py-2">
                  No CPTs yet. Add them manually below.
                </p>
              )}
              {cptEntries.map((c, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 text-sm p-2 rounded-md border bg-card"
                >
                  <Badge
                    variant={c.section === 'primary' ? 'default' : 'outline'}
                    className="text-[10px] cursor-pointer select-none"
                    onClick={() => toggleCptSection(i)}
                    title="Click to toggle primary/secondary"
                  >
                    {c.section === 'primary' ? 'Primary' : 'Secondary'}
                  </Badge>
                  <span className="font-mono font-medium text-xs">{c.code}</span>
                  <span className="text-muted-foreground flex-1 truncate text-xs">
                    {c.description}
                  </span>
                  {c.default_modifiers.length > 0 && (
                    <Badge variant="secondary" className="text-[9px]">
                      {c.default_modifiers.join(', ')}
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    onClick={() => removeCpt(i)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}

              {/* Manual add row */}
              <div className="flex gap-2 pt-1">
                <Input
                  value={newCode}
                  onChange={e => setNewCode(e.target.value)}
                  placeholder="CPT"
                  className="w-24"
                  onKeyDown={e => e.key === 'Enter' && addManualCpt()}
                />
                <Input
                  value={newDesc}
                  onChange={e => setNewDesc(e.target.value)}
                  placeholder="Description"
                  className="flex-1"
                  onKeyDown={e => e.key === 'Enter' && addManualCpt()}
                />
                <Select value={newSection} onValueChange={v => setNewSection(v as 'primary' | 'secondary')}>
                  <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">Primary</SelectItem>
                    <SelectItem value="secondary">Secondary</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={addManualCpt}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Diagnosis codes */}
            {diagnosisCodes.length > 0 && (
              <>
                <Separator />
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">Diagnosis Codes (ICD-10)</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {diagnosisCodes.map((d, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {d.code} — {d.description}
                      </Badge>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Narrative template preview */}
            {narrativeTemplate && (
              <>
                <Separator />
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Narrative Template</Label>
                    <Badge variant="secondary" className="text-[9px]">Variable-based</Badge>
                  </div>
                  <Textarea
                    value={narrativeTemplate}
                    onChange={e => setNarrativeTemplate(e.target.value)}
                    className="min-h-[150px] font-mono text-xs"
                  />
                  <div className="flex flex-wrap gap-1 mt-1">
                    {['{{PATIENT_NAME}}', '{{DOB}}', '{{PROCEDURE_LATERALITY}}', '{{FACILITY_NAME}}'].map(v => (
                      <Badge
                        key={v}
                        variant="secondary"
                        className="text-[9px] cursor-pointer"
                        onClick={() => setNarrativeTemplate(prev => prev + ' ' + v)}
                      >
                        {v}
                      </Badge>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Modifier warning */}
            {cptEntries.some(c => c.section === 'primary' && c.default_modifiers.includes('59')) && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 border border-destructive/20">
                <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                <p className="text-xs text-destructive">
                  Modifier 59 detected on a primary CPT. This is a legacy distinct-service fallback and should not sit on the primary line.
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {step > 1 && (
            <Button variant="outline" onClick={() => setStep(step - 1)}>
              <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />
              Back
            </Button>
          )}
          <div className="flex-1" />
          {step === 1 && (
            <Button onClick={() => setStep(2)} disabled={!canProceedStep1}>
              Next
              <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          )}
          {step === 2 && (
            <Button onClick={() => setStep(3)} disabled={!canProceedStep2}>
              Next — Review
              <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          )}
          {step === 3 && (
            <Button onClick={handleSave} disabled={cptEntries.length === 0 || isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Check className="w-3.5 h-3.5 mr-1.5" />
                  Save Template
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
